import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

const panelStyles = {
    container: {
        position: 'absolute',
        top: '64px', // Positioned 12px below the Map Layer button (which is at top 12px, height 40px)
        left: '12px',
        zIndex: 99,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    toggleBtn: {
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        border: 'none',
        background: 'var(--bs-body-bg, #fff)',
        color: 'var(--bs-body-color, #333)',
        boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px',
        transition: 'background 0.15s, color 0.15s',
    },
    panel: {
        position: 'absolute',
        top: '48px',
        left: 0,
        background: 'var(--bs-body-bg, #fff)',
        color: 'var(--bs-body-color, #212529)',
        borderRadius: '10px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
        padding: '14px 16px',
        minWidth: '220px',
        maxHeight: 'min(380px, 55vh)',
        overflowY: 'auto',
        scrollbarWidth: 'thin',
        msOverflowStyle: 'none',
        zIndex: 10002,
    },
    title: {
        fontSize: '11px',
        fontWeight: 700,
        color: 'var(--bs-secondary-color, #64748b)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '10px',
        display: 'flex',
        justifyContent: 'between',
        alignItems: 'center',
    },
    searchBar: {
        width: '100%',
        height: '28px',
        fontSize: '12px',
        padding: '4px 8px',
        borderRadius: '6px',
        border: '1px solid #cbd5e1',
        marginBottom: '8px',
        outline: 'none',
    },
    districtSelect: {
        width: '100%',
        height: '28px',
        fontSize: '12px',
        padding: '2px 6px',
        borderRadius: '6px',
        border: '1px solid #cbd5e1',
        marginBottom: '8px',
        outline: 'none',
        background: 'var(--bs-body-bg, #fff)',
        color: 'var(--bs-body-color, #212529)',
        cursor: 'pointer',
    },
    scrollArea: {
        maxHeight: '140px',
        overflowY: 'auto',
        paddingRight: '2px',
        marginBottom: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '4px 0',
    },
    label: {
        fontSize: '13px',
        color: 'var(--bs-body-color, #334155)',
        cursor: 'pointer',
        userSelect: 'none',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        maxWidth: '140px',
    },
    divider: {
        height: '1px',
        background: 'var(--bs-border-color, #f1f5f9)',
        margin: '8px 0',
    },
};

// Toggle switch styled identically to MapLayerPanel
const ToggleSwitch = ({ checked, onChange }) => {
    const trackStyle = {
        position: 'relative',
        width: '32px',
        height: '18px',
        borderRadius: '9px',
        background: checked ? '#3b82f6' : '#cbd5e1',
        cursor: 'pointer',
        transition: 'background 0.2s',
        flexShrink: 0,
    };
    const thumbStyle = {
        position: 'absolute',
        top: '2px',
        left: checked ? '16px' : '2px',
        width: '14px',
        height: '14px',
        borderRadius: '50%',
        background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        transition: 'left 0.2s',
    };

    return (
        <div style={trackStyle} onClick={() => onChange(!checked)}>
            <div style={thumbStyle} />
        </div>
    );
};

ToggleSwitch.propTypes = {
    checked: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
};

const MapCoveragePanel = ({
    citiesList = [],
    selectedCities = [],
    onCityToggle,
    onResetCoverage,
    onSelectedCitiesChange,
    analysisLoading = false,
}) => {
    const [expanded, setExpanded] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDistrict, setSelectedDistrict] = useState('');
    const anchorRef = useRef(null);
    const panelRef = useRef(null);

    useEffect(() => {
        if (!expanded) return;
        const handleMouseDown = (e) => {
            if (
                anchorRef.current?.contains(e.target) ||
                panelRef.current?.contains(e.target)
            ) return;
            setExpanded(false);
        };
        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, [expanded]);

    // Extract sorted unique list of districts
    const districts = Array.from(
        new Set(citiesList.map(c => c.district).filter(Boolean))
    ).sort();

    // Filter cities by selected district first
    const districtFilteredCities = citiesList.filter(c => {
        if (!selectedDistrict) return true;
        return (c.district || '').toLowerCase() === selectedDistrict.toLowerCase();
    });

    const sortedCities = [...districtFilteredCities].sort((a, b) => {
        const aCity = a.city || '';
        const bCity = b.city || '';
        const aSelected = selectedCities.includes(aCity);
        const bSelected = selectedCities.includes(bCity);
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return aCity.localeCompare(bCity);
    });

    const filteredCities = sortedCities.filter(c => {
        const cityName = c.city || '';
        return cityName.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const handleSelectAllInDistrict = () => {
        const targetCityNames = filteredCities.map(c => c.city).filter(Boolean);
        const combined = Array.from(new Set([...selectedCities, ...targetCityNames]));
        if (typeof onSelectedCitiesChange === 'function') {
            onSelectedCitiesChange(combined);
        }
    };

    const handleDeselectAllInDistrict = () => {
        const targetCityNames = new Set(filteredCities.map(c => c.city).filter(Boolean));
        const remaining = selectedCities.filter(cityName => !targetCityNames.has(cityName));
        if (typeof onSelectedCitiesChange === 'function') {
            onSelectedCitiesChange(remaining);
        }
    };

    return (
        <div style={panelStyles.container} ref={anchorRef}>
            <button
                type="button"
                style={{
                    ...panelStyles.toggleBtn,
                    background: expanded ? '#3b82f6' : 'var(--bs-body-bg, #fff)',
                    color: expanded ? '#fff' : 'var(--bs-body-color, #333)',
                }}
                onClick={() => setExpanded(prev => !prev)}
                title="Coverage Analysis"
            >
                <span role="img" aria-label="coverage">📍</span>
            </button>
            {expanded && (
                <div ref={panelRef} style={panelStyles.panel}>
                    <div style={panelStyles.title} className="d-flex justify-content-between align-items-center">
                        <span>Coverage Analysis</span>
                        {analysisLoading && <i className="fa fa-refresh fa-spin text-muted" />}
                    </div>

                    {/* District Selector */}
                    <select
                        style={panelStyles.districtSelect}
                        value={selectedDistrict}
                        onChange={(e) => {
                            setSelectedDistrict(e.target.value);
                            setSearchTerm(''); // Clear search when switching districts
                        }}
                    >
                        <option value="">All Districts</option>
                        {districts.map(d => (
                            <option key={d} value={d}>
                                {d}
                            </option>
                        ))}
                    </select>

                    {/* City Search */}
                    <input
                        type="text"
                        style={panelStyles.searchBar}
                        placeholder="Search cities..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />

                    {/* Select/Deselect All buttons for selected district */}
                    {selectedDistrict && (
                        <div className="d-flex justify-content-between align-items-center mb-2 px-1" style={{ fontSize: '11px' }}>
                            <button
                                type="button"
                                className="btn btn-xs btn-link text-primary p-0"
                                style={{ textDecoration: 'none', fontWeight: 600, fontSize: '11px' }}
                                onClick={handleSelectAllInDistrict}
                            >
                                Select All ({filteredCities.length})
                            </button>
                            <button
                                type="button"
                                className="btn btn-xs btn-link text-danger p-0"
                                style={{ textDecoration: 'none', fontWeight: 600, fontSize: '11px' }}
                                onClick={handleDeselectAllInDistrict}
                            >
                                Deselect All
                            </button>
                        </div>
                    )}

                    {/* Scrollable list of cities with Toggle Switches */}
                    <div style={panelStyles.scrollArea}>
                        {filteredCities.map((c) => {
                            const isChecked = selectedCities.includes(c.city);
                            return (
                                <div key={c.city} style={panelStyles.row}>
                                    <span 
                                        style={panelStyles.label} 
                                        title={`${c.city}${c.district ? ` (${c.district})` : ''}`}
                                        onClick={() => onCityToggle(c.city)}
                                    >
                                        {c.city}
                                    </span>
                                    <ToggleSwitch
                                        checked={isChecked}
                                        onChange={() => onCityToggle(c.city)}
                                    />
                                </div>
                            );
                        })}
                        {filteredCities.length === 0 && (
                            <div className="text-muted small text-center italic py-2">No matching cities</div>
                        )}
                    </div>

                    {/* Clear Button */}
                    {selectedCities.length > 0 && (
                        <>
                            <div style={panelStyles.divider} />
                            <div className="text-right">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-link text-danger p-0"
                                    style={{ fontSize: '11px', textDecoration: 'none' }}
                                    onClick={() => {
                                        onResetCoverage();
                                        setSearchTerm('');
                                        setSelectedDistrict('');
                                    }}
                                >
                                    <i className="fa fa-times-circle mr-1" /> Reset Coverage
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

MapCoveragePanel.propTypes = {
    citiesList: PropTypes.array,
    selectedCities: PropTypes.array,
    onCityToggle: PropTypes.func.isRequired,
    onResetCoverage: PropTypes.func.isRequired,
    onSelectedCitiesChange: PropTypes.func.isRequired,
    analysisLoading: PropTypes.bool,
};

export default MapCoveragePanel;
